import type { IntegrationTestResult, SettingsHistoryEntry } from "../../../domain/settings/system-settings.js";
import type { StoredSettingsRecord, SystemSettingsRepository } from "../../../repositories/system-settings-repository.js";

const RECORD_KEY = "inspect_system_settings";
const HISTORY_KEY = "inspect_system_settings_history";
const SEQUENCES_KEY = "inspect_document_sequences";
const TESTS_KEY = "inspect_integration_tests";

function read<T>(key: string, fallback: T): T {
  try {
    if (typeof localStorage === "undefined") return fallback;
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
}

/**
 * Modo navegador (`npm run dev`, sem servidor) — como TODOS os repositórios
 * desse modo, em memória e espelhado no localStorage. Não é a fonte de
 * verdade da plataforma: no modo servidor (homologação/produção) quem vale é
 * o `SqliteSystemSettingsRepository`.
 *
 * O contador de numeração é atômico DENTRO da aba (JavaScript é de uma
 * thread só e o incremento é síncrono); duas abas do mesmo navegador não são
 * coordenadas — limite do modo, declarado.
 */
export class InMemorySystemSettingsRepository implements SystemSettingsRepository {
  private record: StoredSettingsRecord | null = read<StoredSettingsRecord | null>(RECORD_KEY, null);
  private history: SettingsHistoryEntry[] = read<SettingsHistoryEntry[]>(HISTORY_KEY, []);
  private sequences: Record<string, number> = read<Record<string, number>>(SEQUENCES_KEY, {});
  private tests: Record<string, IntegrationTestResult> = read<Record<string, IntegrationTestResult>>(TESTS_KEY, {});

  async load(): Promise<StoredSettingsRecord | null> {
    this.record = read<StoredSettingsRecord | null>(RECORD_KEY, this.record);
    return this.record ? { ...this.record } : null;
  }

  async saveIfVersion(payload: unknown, expectedVersion: number, meta: { updatedAt: string; updatedBy: string }): Promise<boolean> {
    const current = read<StoredSettingsRecord | null>(RECORD_KEY, this.record);
    const currentVersion = current?.version ?? 0;
    if (currentVersion !== expectedVersion) return false;
    const next: StoredSettingsRecord = { payload, version: currentVersion + 1, updatedAt: meta.updatedAt, updatedBy: meta.updatedBy };
    write(RECORD_KEY, next);
    this.record = next;
    return true;
  }

  async appendHistory(entries: readonly SettingsHistoryEntry[]): Promise<void> {
    this.history = [...read<SettingsHistoryEntry[]>(HISTORY_KEY, this.history), ...entries];
    write(HISTORY_KEY, this.history);
  }

  async listHistory(limit = 500): Promise<SettingsHistoryEntry[]> {
    this.history = read<SettingsHistoryEntry[]>(HISTORY_KEY, this.history);
    return [...this.history].sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id)).slice(0, limit);
  }

  async nextSequence(scopeKey: string, seed: number): Promise<number> {
    // Lê, incrementa e grava sem nenhum `await` no meio: dentro da aba, nada intercala.
    this.sequences = read<Record<string, number>>(SEQUENCES_KEY, this.sequences);
    const current = this.sequences[scopeKey];
    const next = (current === undefined ? Math.max(0, seed) : current) + 1;
    this.sequences[scopeKey] = next;
    write(SEQUENCES_KEY, this.sequences);
    return next;
  }

  async recordIntegrationTest(integrationId: string, result: IntegrationTestResult): Promise<void> {
    this.tests = { ...read<Record<string, IntegrationTestResult>>(TESTS_KEY, this.tests), [integrationId]: result };
    write(TESTS_KEY, this.tests);
  }

  async listIntegrationTests(): Promise<Record<string, IntegrationTestResult>> {
    this.tests = read<Record<string, IntegrationTestResult>>(TESTS_KEY, this.tests);
    return { ...this.tests };
  }

  async probe(): Promise<{ ok: boolean; message: string }> {
    if (typeof localStorage === "undefined") return { ok: true, message: "Armazenamento em memória (sem localStorage neste ambiente)." };
    const key = "inspect_settings_probe";
    try {
      const token = `probe-${Date.now()}`;
      localStorage.setItem(key, token);
      const back = localStorage.getItem(key);
      localStorage.removeItem(key);
      return back === token ? { ok: true, message: "Gravação, leitura e remoção no armazenamento do navegador funcionando." } : { ok: false, message: "O valor lido não confere com o gravado." };
    } catch (e) {
      return { ok: false, message: `Armazenamento do navegador recusou a gravação: ${(e as Error)?.message || "motivo não informado"}.` };
    }
  }
}
